import { z } from 'zod';

/**
 * Direção da sincronização
 */
export const syncDirectionSchema = z.enum(['firebird_to_supabase', 'supabase_to_firebird']);

export type SyncDirection = z.infer<typeof syncDirectionSchema>;

/**
 * Status da sincronização
 */
export const syncStatusSchema = z.enum([
  'pending',
  'in_progress',
  'completed',
  'partial',
  'failed',
  'conflict'
]);

export type SyncStatus = z.infer<typeof syncStatusSchema>;

/**
 * Entidades sincronizáveis
 */
export const syncEntitySchema = z.enum([
  'vendedores',
  'clientes',
  'produtos',
  'tabelas_preco',
  'condicoes_pagto',
  'condicoes_pagto_parcelas',
  'empresas',
  'origens_cliente',
  'origens_pedido',
  'pontos_estoque',
  'produtos_estoque',
  'ramos_atividade',
  'clientes_vendedores',
  'tipos_comercializacao',
  'tipos_movimento_receber',
  'pedidos'
]);

export type SyncEntity = z.infer<typeof syncEntitySchema>;

/**
 * Configuração de sincronização por entidade
 * AIDEV-NOTE: Intervalos em segundos
 */
export const syncConfigSchema = z.object({
  entity: syncEntitySchema,
  enabled: z.boolean().default(true),
  direction: syncDirectionSchema,
  interval_seconds: z.number().int().positive(),
  batch_size: z.number().int().positive().default(100),
  retry_attempts: z.number().int().min(0).default(3),
  retry_delay_ms: z.number().int().positive().default(1000)
});

export type SyncConfig = z.infer<typeof syncConfigSchema>;

/**
 * Log de sincronização (tabela sync_log)
 */
export const syncLogSchema = z.object({
  id: z.string().uuid(),
  entity: syncEntitySchema,
  direction: syncDirectionSchema,
  status: syncStatusSchema,
  started_at: z.string().datetime(),
  completed_at: z.string().datetime().nullable(),
  records_processed: z.number().int().min(0),
  records_success: z.number().int().min(0),
  records_failed: z.number().int().min(0),
  error_message: z.string().nullable(),
  last_sync_timestamp: z.string().datetime().nullable(),
  client_id: z.number().int().positive()
});

export type SyncLog = z.infer<typeof syncLogSchema>;

/**
 * Entrada para criar log de sincronização
 */
export const createSyncLogSchema = z.object({
  entity: syncEntitySchema,
  direction: syncDirectionSchema,
  client_id: z.number().int().positive()
});

export type CreateSyncLogInput = z.infer<typeof createSyncLogSchema>;

/**
 * Resultado de uma operação de sync
 */
export const syncOperationResultSchema = z.object({
  success: z.boolean(),
  records_synced: z.number().int().min(0),
  records_failed: z.number().int().min(0),
  errors: z.array(
    z.object({
      record_id: z.union([z.string(), z.number().int()]),
      error: z.string()
    })
  ),
  duration_ms: z.number().int().positive()
});

export type SyncOperationResult = z.infer<typeof syncOperationResultSchema>;

/**
 * Status geral do sync
 */
export const syncStatusSummarySchema = z.object({
  last_sync_at: z.record(syncEntitySchema, z.string().datetime().nullable()),
  next_sync_at: z.record(syncEntitySchema, z.string().datetime().nullable()),
  pending_records: z.record(syncEntitySchema, z.number().int().min(0)),
  is_syncing: z.boolean()
});

export type SyncStatusSummary = z.infer<typeof syncStatusSummarySchema>;

/**
 * Pedido para sincronizar (Supabase → Firebird)
 * AIDEV-NOTE: ID_PEDIDO_INTEGRACAO armazena o UUID do Supabase para idempotência
 */
export const pedidoSyncSchema = z.object({
  id: z.string().uuid(),
  numero: z.number().int().positive().nullable(), // Null até sincronizar
  cliente_id: z.number().int().positive(),
  vendedor_id: z.number().int().positive(),
  status: z.enum(['rascunho', 'finalizado', 'sincronizando', 'sincronizado', 'erro_sync']),
  itens: z.array(
    z.object({
      produto_id: z.number().int().positive(),
      quantidade: z.number().positive(),
      preco_unitario: z.number().nonnegative(),
      desconto: z.number().nonnegative().default(0)
    })
  ),
  observacoes: z.string().nullable(),
  id_pedido_integracao: z.string().uuid().nullable(), // Para idempotência
  sync_attempts: z.number().int().min(0).default(0),
  last_sync_error: z.string().nullable(),
  created_at: z.string().datetime()
});

export type PedidoSync = z.infer<typeof pedidoSyncSchema>;

/**
 * Configuração de sync de pedido por usuário
 */
export const configSyncPedidoSchema = z.object({
  usuario_id: z.string().uuid(),
  modo_sync: z.enum(['automatico', 'manual']).default('manual'),
  ultimo_sync_em: z.string().datetime().nullable()
});

export type ConfigSyncPedido = z.infer<typeof configSyncPedidoSchema>;
